import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoConfig {
  title?: string;
  description?: string;
  ogTitle?: string;
  robots?: string;
  canonicalUrl?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly defaultRobots = 'index, follow';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  setPageSeo(config: SeoConfig): void {
    const title = this.normalize(config.title);
    const description = this.normalize(config.description);
    const ogTitle = this.normalize(config.ogTitle) || title;
    const robots = this.normalize(config.robots) || this.defaultRobots;

    if (title) {
      this.titleService.setTitle(title);
    }

    if (description) {
      this.metaService.updateTag({ name: 'description', content: description });
    }

    if (ogTitle) {
      this.metaService.updateTag({ property: 'og:title', content: ogTitle });
    }

    this.metaService.updateTag({ name: 'robots', content: robots });

    const canonicalUrl = this.normalize(config.canonicalUrl);
    if (canonicalUrl) {
      this.setCanonicalUrl(canonicalUrl);
    }
  }

  private setCanonicalUrl(url: string): void {
    const head = this.document.head;
    if (!head) {
      return;
    }

    let linkEl = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = this.document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      head.appendChild(linkEl);
    }

    linkEl.setAttribute('href', this.toAbsoluteUrl(url));
  }

  private normalize(value?: string): string {
    return String(value ?? '').trim();
  }

  private toAbsoluteUrl(url: string): string {
    try {
      const base = this.document.baseURI || this.document.location?.href || 'http://localhost/';
      return new URL(url, base).href;
    } catch {
      return url;
    }
  }
}
